<script setup lang="ts">
import { useCompanyStore, ViewCompanyType } from '@/shared/stores/CompanyStore'
import CompanySkeleton from '@/features/companies/components/company-detail/CompanySkeleton.vue'
import CompanyDetailHeader from '@/features/companies/components/company-detail/CompanyDetailHeader.vue'
import AddLeadFormDialog from '@/features/companies/dialogs/AddLeadFormDialog.vue'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import CreateSubCompanyFormDialog from '../dialogs/CreateSubCompanyFormDialog.vue'
import { usePermissions } from '@/shared/composables/UsePermissions'
import { useTopBarTitle } from '@/shared/composables/UseTopBarTitle'

definePage({
  path: '/companies/:id',
  name: 'companies.[id]',
  meta: {
    title: 'Bedrijf',
    breadcrumbParent: 'companies.overview',
    permission: {
      resource: 'Company',
      level: 'Read',
    },
  },
})

const companyStore = useCompanyStore()
const router = useRouter()

const companyId = ref<number>(-1)
const company = ref<ViewCompanyType | undefined>(undefined)
const companyIsPending = ref<boolean>(true)

const route = useRoute('companies.[id]')

async function loadCompany(id: number) {
  companyIsPending.value = true
  try {
    company.value = await companyStore.getCompany(id)
  }
  finally {
    companyIsPending.value = false
  }
}

// The topbar shows the company name as the last breadcrumb once loaded.
useTopBarTitle(() => company.value?.name)

async function reloadCompany() {
  await loadCompany(companyId.value)
}

provide<Ref<ViewCompanyType | undefined>>('company', company)
provide<Ref<number>>('companyId', companyId)
provide<Ref<boolean>>('companyIsPending', companyIsPending)
provide<() => Promise<void>>('reloadCompany', reloadCompany)

onBeforeMount(async () => {
  companyId.value = Number.parseInt((route.params as { id: string }).id)
  await loadCompany(companyId.value)
  if (route.name === 'companies.[id]') {
    router.replace({
      name: 'companies.[id].overview' as any,
      params: { id: companyId.value },
    })
  }
})

watch(route, async (route) => {
  const newId = Number.parseInt((route.params as { id: string }).id)
  const oldId = companyId.value
  if (newId !== oldId && newId !== -1) {
    company.value = undefined
    companyId.value = newId
    await loadCompany(newId)
  }
})

watch(() => route.name, (name) => {
  if (name === 'companies.[id]') {
    router.replace({
      name: 'companies.[id].overview' as any,
      params: { id: companyId.value },
    })
  }
})

const dialog = useDynamicDialog()

// A new AI vacancy is composed on the AI-agents page, with this company preselected.
function addAiJob() {
  router.push({
    name: '/tools/AiAgents',
    query: { companyId: String(companyId.value) },
  })
}

function addLead() {
  dialog.open(AddLeadFormDialog, {
    data: {
      companyId: company.value?.id,
    },
    async onClose() {
      await reloadCompany()
    },
  })
}

function openCreateSubCompanyDialog() {
  dialog.open(CreateSubCompanyFormDialog, {
    data: {
      parentCompanyId: company.value?.id,
      parentCompanyName: company.value?.name,
      employers: company.value?.users,
    },
  })
}

provide<() => void>('addLead', addLead)
provide<() => void>('addAiJob', addAiJob)
provide<() => void>('openCreateSubCompanyDialog', openCreateSubCompanyDialog)

const { can } = usePermissions()
const canReadJobs = computed(() => can('Job', 'Read'))
const canReadStatistics = computed(() => can('CompanyStatistics', 'Read'))
const canReadInvoicing = computed(() => can('CompanyInvoicing', 'Read'))

/*
 * The overview is open to every internal employee; the other tabs follow the permission of the data
 * they show (jobs, company statistics, invoicing).
 */
const tabs = computed(() => [
  {
    label: 'Overzicht',
    name: 'companies.[id].overview',
  },
  ...(canReadJobs.value
    ? [
      {
        label: 'Vacatures',
        name: 'companies.[id].vacatures',
      },
    ]
    : []),
  {
    label: 'Customer Success',
    name: 'companies.[id].customer-success',
  },
  ...(canReadStatistics.value
    ? [
      {
        label: 'Statistieken',
        name: 'companies.[id].statistics',
      }, 
    ]
    : []),
  ...(canReadInvoicing.value
    ? [
      {
        label: 'Facturatie',
        name: 'companies.[id].facturatie',
      }, 
    ]
    : []),
])
</script>

<template>
  <div v-if="company" class="space-y-5">
    <CompanyDetailHeader :company="company" @updated="reloadCompany" />

    <section>
      <Tabs :value="route.name" scrollable>
        <TabList>
          <router-link
            v-for="tab in tabs"
            :key="tab.name"
            :to="{ name: tab.name as any, params: { id: companyId } }"
          >
            <Tab :value="tab.name">
              {{ tab.label }}
            </Tab>
          </router-link>
        </TabList>
      </Tabs>

      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
    </section>
  </div>
  <div v-else>
    <CompanySkeleton />
  </div>
</template>
